/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/marketplace.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * The closed set of metadata a plugin may be handed. Each is a bounded slice the host assembles,
 * never a query the plugin composes — an open vocabulary, or a capability meaning "run this SQL",
 * would put the boundary back in the plugin's hands. Note what is absent and cannot be added
 * without changing the enum under review: no connection, no endpoint, no credential, no DSN, no
 * row, no sample, nothing cross-tenant.
 */
export type Capability = 'dataset.schema' | 'quality.score' | 'rule.results';

export const CAPABILITYS: readonly Capability[] = ['dataset.schema', 'quality.score', 'rule.results'];

export function isCapability(value: unknown): value is Capability {
  return (['dataset.schema', 'quality.score', 'rule.results'] as readonly unknown[]).includes(value);
}

/**
 * Only `published` runs, and it is reachable only through a passed review and two signatures.
 * `rejected` and `revoked` are both terminal, and they are **not** the same fact. `rejected` means
 * a review refused an artifact that never ran anywhere. `revoked` means something that *did* run
 * in production has been withdrawn — a different situation with a different audience, and
 * collapsing them would make "was this ever live" a question the registry could not answer, which
 * is the first thing an incident asks. A revoked plugin stays visible: it is not installable and
 * not runnable, and its existing installation rows are deliberately preserved, because a grant
 * somebody made is evidence about a decision.
 */
export type PluginState = 'submitted' | 'reviewed' | 'approved' | 'published' | 'rejected' | 'revoked';

export const PLUGIN_STATES: readonly PluginState[] = ['submitted', 'reviewed', 'approved', 'published', 'rejected', 'revoked'];

export function isPluginState(value: unknown): value is PluginState {
  return (['submitted', 'reviewed', 'approved', 'published', 'rejected', 'revoked'] as readonly unknown[]).includes(value);
}

/**
 * Three values, not a boolean. "Not yet reviewed" and "reviewed and safe" must be different states
 * — collapsing them is what lets an unreviewed plugin look cleared.
 */
export type ReviewVerdict = 'pending' | 'pass' | 'fail';

export const REVIEW_VERDICTS: readonly ReviewVerdict[] = ['pending', 'pass', 'fail'];

export function isReviewVerdict(value: unknown): value is ReviewVerdict {
  return (['pending', 'pass', 'fail'] as readonly unknown[]).includes(value);
}

export interface ReviewFinding {
  readonly code: string;
  /** A blocking finding makes the verdict `fail`, and the plugin can never run. */
  readonly severity: 'blocking' | 'advisory';
  readonly detail: string;
}

export function isReviewFinding(value: unknown): value is ReviewFinding {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['code'] === 'string' &&
    (['blocking', 'advisory'] as readonly unknown[]).includes(candidate['severity']) &&
    typeof candidate['detail'] === 'string'
  );
}

export interface Plugin {
  readonly id: string;
  readonly name: string;
  readonly version: string;
  /** SHA-256 of the uploaded module. What the two publication signatures cover. */
  readonly artifact_hash: string;
  /** What the author says it needs — not what any tenant grants. */
  readonly declared_capabilities: readonly Capability[];
  readonly state: PluginState;
  readonly review_verdict: ReviewVerdict;
  readonly runnable: boolean;
  /**
   * When, and deliberately not by whom. The registry is platform-scoped — one review per artifact
   * rather than one per customer — so every field on it is readable by every tenant on the
   * deployment, and the submitter's subject is a named person at a named organisation. It stays on
   * the row and in the audit chain, which are tenant-scoped; it is not on this surface.
   */
  readonly submitted_at: string;
  readonly review_findings: readonly ReviewFinding[];
}

export function isPlugin(value: unknown): value is Plugin {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['name'] === 'string' &&
    typeof candidate['version'] === 'string' &&
    typeof candidate['artifact_hash'] === 'string' &&
    Array.isArray(candidate['declared_capabilities']) &&
    (candidate['declared_capabilities'] as readonly unknown[]).every((item) => isCapability(item)) &&
    (['submitted', 'reviewed', 'approved', 'published', 'rejected', 'revoked'] as readonly unknown[]).includes(candidate['state']) &&
    (['pending', 'pass', 'fail'] as readonly unknown[]).includes(candidate['review_verdict']) &&
    typeof candidate['runnable'] === 'boolean' &&
    typeof candidate['submitted_at'] === 'string' &&
    Array.isArray(candidate['review_findings']) &&
    (candidate['review_findings'] as readonly unknown[]).every((item) => isReviewFinding(item))
  );
}

export interface Installation {
  readonly plugin_id: string;
  readonly granted_capabilities: readonly Capability[];
  readonly enabled: boolean;
  readonly installed_by: string;
  readonly installed_at: string;
}

export function isInstallation(value: unknown): value is Installation {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['plugin_id'] === 'string' &&
    Array.isArray(candidate['granted_capabilities']) &&
    (candidate['granted_capabilities'] as readonly unknown[]).every((item) => isCapability(item)) &&
    typeof candidate['enabled'] === 'boolean' &&
    typeof candidate['installed_by'] === 'string' &&
    typeof candidate['installed_at'] === 'string'
  );
}

export interface PluginRunFinding {
  readonly code: string;
  readonly severity: string;
  /**
   * Capped. A plugin returning a large blob is a plugin moving its input back out one message at a
   * time.
   */
  readonly message: string;
}

export function isPluginRunFinding(value: unknown): value is PluginRunFinding {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['code'] === 'string' &&
    typeof candidate['severity'] === 'string' &&
    typeof candidate['message'] === 'string'
  );
}

export interface RunResult {
  /**
   * The durable run record this response points at. **Declared here because the route has always
   * sent it and this schema did not**, so a client generated from the contract dropped the one
   * field that joins a run to its own history — and a caller could not fetch the findings again
   * without it. The same class as the `subject_kind` divergence in `observability.yaml`: a spec
   * narrower than the API it describes.
   */
  readonly run_id: string;
  readonly dataset_id: string;
  /**
   * Sent by the route and previously undeclared. Without it a client cannot tell a succeeded run
   * from a refused one, which is the whole verdict of the request.
   */
  readonly status: RunStatus;
  readonly duration_ms: number;
  /**
   * What the plugin was actually handed. Narrower than the grant when the caller lacks a
   * permission governing one — reporting the grant instead would overstate what it saw.
   */
  readonly capabilities_used: readonly Capability[];
  readonly truncated: boolean;
  readonly findings: readonly PluginRunFinding[];
}

export function isRunResult(value: unknown): value is RunResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['run_id'] === 'string' &&
    typeof candidate['dataset_id'] === 'string' &&
    (['succeeded', 'refused', 'failed'] as readonly unknown[]).includes(candidate['status']) &&
    typeof candidate['duration_ms'] === 'number' &&
    Array.isArray(candidate['capabilities_used']) &&
    (candidate['capabilities_used'] as readonly unknown[]).every((item) => isCapability(item)) &&
    typeof candidate['truncated'] === 'boolean' &&
    Array.isArray(candidate['findings']) &&
    (candidate['findings'] as readonly unknown[]).every((item) => isPluginRunFinding(item))
  );
}

export interface CapabilityEntry {
  readonly code: Capability;
  /** The `action:resource_type` permission checked before this slice is assembled. */
  readonly governed_by: string;
}

export function isCapabilityEntry(value: unknown): value is CapabilityEntry {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['dataset.schema', 'quality.score', 'rule.results'] as readonly unknown[]).includes(candidate['code']) &&
    typeof candidate['governed_by'] === 'string'
  );
}

export interface CapabilityCatalogue {
  /** ADR-004's ladder position. Always 1. */
  readonly rung: number;
  readonly capabilities: readonly CapabilityEntry[];
  readonly never_granted: readonly string[];
  readonly sandbox: string;
}

export function isCapabilityCatalogue(value: unknown): value is CapabilityCatalogue {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['rung'] === 'number' &&
    Array.isArray(candidate['capabilities']) &&
    (candidate['capabilities'] as readonly unknown[]).every((item) => isCapabilityEntry(item)) &&
    Array.isArray(candidate['never_granted']) &&
    (candidate['never_granted'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['sandbox'] === 'string'
  );
}

/**
 * **Three, because a refusal is not a failure.** `refused` is the platform declining — not
 * published, revoked, not installed, stopped, or an artifact that no longer matches what was
 * approved. Every one of those is the lifecycle working, and counting them as failures would make
 * a correctly-guarded deployment look broken. `failed` is the sandbox unable to complete, which is
 * a fact about the artifact.
 */
export type RunStatus = 'succeeded' | 'refused' | 'failed';

export const RUN_STATUSES: readonly RunStatus[] = ['succeeded', 'refused', 'failed'];

export function isRunStatus(value: unknown): value is RunStatus {
  return (['succeeded', 'refused', 'failed'] as readonly unknown[]).includes(value);
}

/**
 * A closed vocabulary rather than a message, so "how often is this refused because its artifact no
 * longer matches" is a query rather than a text search — and so a sandbox's or a driver's own
 * message can never reach the column, which is where a statement or a tenant literal would arrive.
 * `no_capability_satisfied` is **not** a refusal of the run: the caller held `run` and none of the
 * permissions governing its granted slices, so the document was correctly empty and the plugin
 * ran. It is recorded because a plugin that found nothing against nothing is otherwise
 * indistinguishable from one that found nothing against everything.
 */
export type RunFailureReason = 'not_published' | 'revoked' | 'not_installed' | 'disabled' | 'artifact_mismatch' | 'no_capability_satisfied' | 'sandbox_refused' | 'sandbox_error';

export const RUN_FAILURE_REASONS: readonly RunFailureReason[] = ['not_published', 'revoked', 'not_installed', 'disabled', 'artifact_mismatch', 'no_capability_satisfied', 'sandbox_refused', 'sandbox_error'];

export function isRunFailureReason(value: unknown): value is RunFailureReason {
  return (['not_published', 'revoked', 'not_installed', 'disabled', 'artifact_mismatch', 'no_capability_satisfied', 'sandbox_refused', 'sandbox_error'] as readonly unknown[]).includes(value);
}

export interface PluginRun {
  readonly id: string;
  readonly plugin_id: string;
  /**
   * Copied onto the row rather than joined. The record must say what was true *then*: a plugin
   * revoked next month, or a registry row a future migration reshapes, must not change what this
   * row says ran.
   */
  readonly plugin_name: string;
  readonly plugin_version: string;
  /** The bytes that actually executed, so a run is traceable to reviewed code. */
  readonly artifact_hash: string;
  readonly dataset_id: string;
  /**
   * Who initiated it. Tenant-scoped, so this is not the cross-tenant disclosure `submitted_by`
   * would be.
   */
  readonly actor: string;
  readonly status: RunStatus;
  /**
   * Null exactly when the status is `succeeded`, and set otherwise — a CHECK constraint holds the
   * pair, so a refusal always names its reason and a success never carries one.
   */
  readonly failure_reason: RunFailureReason | null;
  readonly granted_capabilities: readonly Capability[];
  /**
   * What the caller's own permissions actually satisfied, which can be narrower than the grant.
   * **Both sets are published and the difference is the point** — reporting only the grant would
   * overstate what the plugin was shown, which is the one thing this record exists to answer
   * honestly. Never wider than `granted_capabilities`; a CHECK enforces it.
   */
  readonly capabilities_used: readonly Capability[];
  readonly findings_count: number;
  readonly findings_truncated: boolean;
  readonly duration_ms: number;
  readonly ran_at: string;
}

export function isPluginRun(value: unknown): value is PluginRun {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['plugin_id'] === 'string' &&
    typeof candidate['plugin_name'] === 'string' &&
    typeof candidate['plugin_version'] === 'string' &&
    typeof candidate['artifact_hash'] === 'string' &&
    typeof candidate['dataset_id'] === 'string' &&
    typeof candidate['actor'] === 'string' &&
    (['succeeded', 'refused', 'failed'] as readonly unknown[]).includes(candidate['status']) &&
    (candidate['failure_reason'] === null || isRunFailureReason(candidate['failure_reason'])) &&
    Array.isArray(candidate['granted_capabilities']) &&
    (candidate['granted_capabilities'] as readonly unknown[]).every((item) => isCapability(item)) &&
    Array.isArray(candidate['capabilities_used']) &&
    (candidate['capabilities_used'] as readonly unknown[]).every((item) => isCapability(item)) &&
    typeof candidate['findings_count'] === 'number' &&
    typeof candidate['findings_truncated'] === 'boolean' &&
    typeof candidate['duration_ms'] === 'number' &&
    typeof candidate['ran_at'] === 'string'
  );
}

export interface UpgradePlan {
  readonly plugin_name: string;
  readonly from_version: string;
  readonly to_version: string;
  /**
   * The intersection of what this tenant granted with what the target version declares. An upgrade
   * can narrow a grant and can never broaden one.
   */
  readonly carried_capabilities: readonly Capability[];
  /**
   * Capabilities the target version declares that this tenant never granted. **Not granted by the
   * upgrade** — a newer version declaring more is asking for data nobody agreed to hand it, so
   * these need a separate, deliberate install. Named rather than omitted, because seeing them is
   * the reason this read exists.
   */
  readonly not_carried_capabilities: readonly Capability[];
  /** Capabilities this tenant granted that the target version no longer declares. */
  readonly dropped_capabilities: readonly Capability[];
}

export function isUpgradePlan(value: unknown): value is UpgradePlan {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['plugin_name'] === 'string' &&
    typeof candidate['from_version'] === 'string' &&
    typeof candidate['to_version'] === 'string' &&
    Array.isArray(candidate['carried_capabilities']) &&
    (candidate['carried_capabilities'] as readonly unknown[]).every((item) => isCapability(item)) &&
    Array.isArray(candidate['not_carried_capabilities']) &&
    (candidate['not_carried_capabilities'] as readonly unknown[]).every((item) => isCapability(item)) &&
    Array.isArray(candidate['dropped_capabilities']) &&
    (candidate['dropped_capabilities'] as readonly unknown[]).every((item) => isCapability(item))
  );
}
